import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Gift, Share2, Copy, Users, Sparkles, Trophy, MessageCircle } from "lucide-react";

export default function Referral() {
  const { user, lang } = useApp();
  const isIT = lang === "it";
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/referral/me").then(({ data }) => setData(data)).catch(() => {});
    api.get("/referral/leaderboard").then(({ data }) => setBoard(data)).catch(() => {});
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] grain-bg flex items-center justify-center px-6">
        <div className="card-surface p-12 text-center max-w-md">
          <Gift size={48} className="text-[#EAB308] mx-auto mb-4" />
          <h1 className="text-3xl font-black text-[#17301C] mb-3">Referral Program</h1>
          <p className="text-[#3A4A3E] mb-6">{isIT ? "Accedi per ottenere il tuo link personale." : "Log in to get your personal link."}</p>
          <Link to="/login" className="btn-primary" data-testid="referral-login-cta">{isIT ? "Accedi" : "Log in"}</Link>
        </div>
      </div>
    );
  }

  const url = data ? `${window.location.origin}/register?ref=${data.code}` : "";

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success(isIT ? "Link copiato!" : "Link copied!"); }
    catch { toast.error("Copy failed"); }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "SolarControl Minniti",
          text: isIT ? "Prova SolarControl Minniti — 1 mese Pro gratis con il mio link!" : "Try SolarControl Minniti — 1 free Pro month with my link!",
          url,
        });
      } catch { /* user cancelled */ }
    } else { copy(); }
  };

  const wa = `https://wa.me/?text=${encodeURIComponent((isIT ? "Prova SolarControl Minniti, l'app che monitora il fotovoltaico e calcola i tuoi risparmi. Con il mio link hai 1 mese Pro gratis: " : "Try SolarControl Minniti — 1 free Pro month: ") + url)}`;

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain-bg pb-16">
      <div className="max-w-4xl mx-auto px-6 pt-12">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 label-eco mb-4">
            <Gift size={12} className="text-[#EAB308]" /> Referral · SolarControl
          </div>
          <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-[#17301C] leading-[0.95]">
            {isIT ? "Invita un amico," : "Invite a friend,"} <br />
            <span className="italic font-light text-[#EAB308]">{isIT ? "1 mese Pro gratis." : "1 free Pro month."}</span>
          </h1>
          <p className="text-lg text-[#3A4A3E] mt-6 max-w-xl mx-auto">
            {isIT
              ? "Per ogni amico che si registra col tuo link, ricevi 1 mese Pro extra. Anche il tuo amico riceve 1 mese Pro gratis."
              : "For every friend who signs up with your link, you get 1 extra Pro month. Your friend also gets 1 free Pro month."}
          </p>
        </div>

        {data && (
          <>
            {/* KPI */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <StatBig icon={Users} label={isIT ? "Amici invitati" : "Referred friends"} value={data.referred_count} accent />
              <StatBig icon={Sparkles} label={isIT ? "Mesi Pro guadagnati" : "Pro months earned"} value={data.bonus_months_earned || 0} />
              <StatBig icon={Trophy} label={isIT ? "Codice personale" : "Your code"} value={data.code} mono />
            </div>

            {/* SHARE BOX */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-6 mb-8" data-testid="referral-share-box">
              <div className="label-eco mb-3 flex items-center gap-2"><Share2 size={12} /> {isIT ? "Il tuo link personale" : "Your personal link"}</div>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-[#F9F8F6] border border-[#DDE0D8] mb-4">
                <input readOnly value={url} data-testid="referral-url"
                  className="flex-1 bg-transparent text-sm text-[#17301C] font-mono focus:outline-none" />
                <button onClick={copy} data-testid="referral-copy" className="p-2 rounded-lg bg-[#17301C] text-white hover:bg-[#0d1c11] transition-colors">
                  <Copy size={14} />
                </button>
              </div>
              <div className="flex flex-wrap gap-3">
                <button onClick={share} data-testid="referral-share-native" className="btn-primary flex-1 justify-center">
                  <Share2 size={16} /> {isIT ? "Condividi" : "Share"}
                </button>
                <a href={wa} target="_blank" rel="noreferrer noopener" data-testid="referral-whatsapp" className="btn-secondary flex-1 justify-center">
                  <MessageCircle size={16} /> WhatsApp
                </a>
              </div>
            </motion.div>

            {/* LEADERBOARD */}
            {board.length > 0 && (
              <div className="card-surface p-6" data-testid="referral-leaderboard">
                <div className="label-eco mb-4 flex items-center gap-2"><Trophy size={12} className="text-[#EAB308]" /> Leaderboard</div>
                <div className="space-y-2">
                  {board.map((b, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-[#F9F8F6] border border-[#DDE0D8]">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm ${
                        i === 0 ? "bg-[#EAB308] text-[#17301C]" : "bg-[#EAE7E0] text-[#17301C]"
                      }`}>{i + 1}</div>
                      <div className="flex-1 font-semibold text-[#17301C]">{b.name}</div>
                      <div className="text-sm text-[#3A4A3E] font-mono-nums">
                        {b.referral_count} <span className="text-xs text-[#6E7A71]">amici</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const StatBig = ({ icon: Icon, label, value, accent, mono }) => (
  <div className={`p-5 rounded-2xl border ${accent ? "bg-[#17301C] text-white border-[#17301C]" : "bg-white border-[#DDE0D8]"}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={13} className={accent ? "text-[#EAB308]" : "text-[#6E7A71]"} />
      <div className={`text-[10px] uppercase tracking-widest font-bold ${accent ? "text-[#EAB308]" : "text-[#6E7A71]"}`}>{label}</div>
    </div>
    <div className={`${mono ? "text-2xl" : "text-4xl"} font-black font-mono-nums ${accent ? "text-[#EAB308]" : "text-[#17301C]"}`}>
      {value}
    </div>
  </div>
);
